import Stack from "@mui/material/Stack";
import { useEffect, useState } from "react";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
  useWatch,
} from "react-hook-form";

import { TextInput } from "~/components/text_input";
import {
  convertDaysHoursMinutesToSeconds,
  convertSecondsToDaysHoursMinutes,
} from "~/utilities/timing";

interface DaysHoursFieldsProps<T extends FieldValues> {
  fieldName: FieldPath<T>;
  control: Control<T>;
  disabled?: boolean;
  dataTestIdPrefix?: string;
  showMinutes?: boolean;
}

export function DaysHoursFields<T extends FieldValues>({
  fieldName,
  control,
  disabled = false,
  dataTestIdPrefix,
  showMinutes,
}: DaysHoursFieldsProps<T>) {
  const seconds = useWatch({
    control,
    name: fieldName,
  });

  const [daysText, setDaysText] = useState("");
  const [hoursText, setHoursText] = useState("");
  const [minutesText, setMinutesText] = useState("");

  useEffect(() => {
    const { days, hours, minutes } = convertSecondsToDaysHoursMinutes(seconds);

    setDaysText(days !== undefined ? String(days) : "");
    setHoursText(hours !== undefined ? String(hours) : "");
    setMinutesText(minutes !== undefined ? String(minutes) : "");
  }, [seconds]);

  return (
    <Stack direction="row" spacing={2.5}>
      <Controller
        name={fieldName}
        control={control}
        render={({ field }) => {
          return (
            <>
              <TextInput
                value={daysText}
                onChange={(e) => {
                  const newDaysText = e.target.value;
                  setDaysText(newDaysText);
                  if (
                    newDaysText === "" &&
                    hoursText === "" &&
                    minutesText === ""
                  ) {
                    field.onChange(undefined);
                    return;
                  }

                  const newDays = Number(newDaysText);
                  const newMinutes = Number(minutesText);
                  const newHours = Number(hoursText);

                  field.onChange(
                    convertDaysHoursMinutesToSeconds(
                      newDays,
                      newHours,
                      newMinutes,
                    ),
                  );
                }}
                type="number"
                placeholder="Enter Days"
                label="Days"
                slotProps={{ input: { inputProps: { min: 0 } } }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                }}
                disabled={disabled}
                data-testid={
                  dataTestIdPrefix
                    ? `${dataTestIdPrefix}-days-input`
                    : undefined
                }
              />
              <TextInput
                value={hoursText}
                onChange={(e) => {
                  const newHoursText = e.target.value;
                  setHoursText(newHoursText);
                  if (
                    newHoursText === "" &&
                    daysText === "" &&
                    minutesText === ""
                  ) {
                    field.onChange(undefined);
                    return;
                  }

                  const newHours = Number(newHoursText);
                  const newMinutes = Number(minutesText);
                  const newDays = Number(daysText);

                  field.onChange(
                    convertDaysHoursMinutesToSeconds(
                      newDays,
                      newHours,
                      newMinutes,
                    ),
                  );
                }}
                type="number"
                placeholder="Enter Hours"
                label="Hours"
                slotProps={{ input: { inputProps: { min: 0 } } }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                }}
                disabled={disabled}
                data-testid={
                  dataTestIdPrefix
                    ? `${dataTestIdPrefix}-hours-input`
                    : undefined
                }
              />
              {showMinutes && (
                <TextInput
                  value={minutesText}
                  onChange={(e) => {
                    const newMinutesText = e.target.value;
                    setMinutesText(newMinutesText);
                    if (
                      newMinutesText === "" &&
                      daysText === "" &&
                      hoursText === ""
                    ) {
                      field.onChange(undefined);
                      return;
                    }

                    const newMinutes = Number(newMinutesText);
                    const newHours = Number(hoursText);
                    const newDays = Number(daysText);

                    field.onChange(
                      convertDaysHoursMinutesToSeconds(
                        newDays,
                        newHours,
                        newMinutes,
                      ),
                    );
                  }}
                  type="number"
                  placeholder="Enter Minutes"
                  label="Minutes"
                  slotProps={{ input: { inputProps: { min: 0 } } }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                  }}
                  disabled={disabled}
                  data-testid={
                    dataTestIdPrefix
                      ? `${dataTestIdPrefix}-minutes-input`
                      : undefined
                  }
                />
              )}
            </>
          );
        }}
      />
    </Stack>
  );
}
